import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { T, fmt, Card, Boton, Titulo, Aviso, Vacio, Cargando } from '../lib/ui';

// ════════════════════════════════════════════════════════════════════
// Impresión por lote
//
// Los sustentos internos se llenan en la plataforma, se imprimen ya
// completos y se firman a mano. Ese papel firmado es el que sustenta
// el gasto ante SUNAT; su foto vuelve adjunta al gasto, y el gasto
// viaja a SAP con el costo y su documento asociado.
//
// Tres recibos por hoja A4: aprovecha el papel igual que el talonario
// y sale una tira que se corta por las líneas punteadas.
// ════════════════════════════════════════════════════════════════════

// Alto util de una A4 menos margenes, repartido segun cuantos entran.
const FORMATOS = {
  recibo_ad:          { porHoja: 3, alto: 88, etiqueta: 'Recibo interno' },
  planilla_movilidad: { porHoja: 2, alto: 133, etiqueta: 'Planilla de movilidad' },
};

export function ImprimirLote({ usuario, onCerrar }) {
  const [lista, setLista] = useState(null);
  const [elegidos, setElegidos] = useState(new Set());
  const [docs, setDocs] = useState(null);
  const [soloSinImprimir, setSoloSinImprimir] = useState(true);
  const [aviso, setAviso] = useState(null);

  useEffect(() => {
    setLista(null);
    api.documentosPendientes(soloSinImprimir)
      .then((r) => setLista(r.documentos || []))
      .catch(() => setLista([]));
  }, [soloSinImprimir]);

  const alternar = (id) => {
    const s = new Set(elegidos);
    if (s.has(id)) s.delete(id); else s.add(id);
    setElegidos(s);
  };

  // Solo se agrupan documentos del mismo tipo: mezclar un recibo con
  // una planilla en la misma tira deja huecos y cortes torcidos.
  const porTipo = (lista || []).reduce((acc, d) => {
    (acc[d.tipoDocumento] = acc[d.tipoDocumento] || []).push(d);
    return acc;
  }, {});

  const tiposElegidos = new Set(
    (lista || []).filter((d) => elegidos.has(d.id)).map((d) => d.tipoDocumento),
  );

  const preparar = async () => {
    if (tiposElegidos.size > 1) {
      return setAviso({
        tipo: 'warn',
        texto: 'Elige documentos de un solo tipo por tanda: los recibos y las '
             + 'planillas ocupan distinto alto y no se pueden cortar juntos.',
      });
    }
    const ids = (lista || []).filter((d) => elegidos.has(d.id)).map((d) => d.id);
    if (ids.length === 0) return;

    try {
      const r = await api.documentosLote(ids);
      setDocs(r.documentos || []);
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudieron preparar los documentos.' });
    }
  };

  if (docs) {
    return (
      <HojaImpresion
        documentos={docs}
        onCerrar={() => setDocs(null)}
        onImpreso={async () => {
          await api.marcarImpresos(docs.map((d) => d.gasto.id)).catch(() => {});
          setElegidos(new Set());
          setDocs(null);
          setAviso({ tipo: 'ok', texto: 'Documentos marcados como impresos.' });
          api.documentosPendientes(soloSinImprimir)
            .then((r) => setLista(r.documentos || [])).catch(() => {});
        }}
      />
    );
  }

  if (!lista) return <Cargando />;

  return (
    <>
      <Titulo sub="Se llenan aquí, se imprimen completos y se firman a mano">
        Imprimir documentos
      </Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      <div style={{
        display: 'flex', gap: 8, marginBottom: 14,
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        <button onClick={() => setSoloSinImprimir(!soloSinImprimir)} style={{
          padding: '6px 13px', borderRadius: 15, fontSize: 12, cursor: 'pointer',
          border: `1px solid ${soloSinImprimir ? T.navy : T.border}`,
          background: soloSinImprimir ? T.navy : T.paper,
          color: soloSinImprimir ? '#fff' : T.text,
        }}>
          Solo sin imprimir
        </button>
        <span style={{ fontSize: 12, color: T.muted }}>
          {elegidos.size} seleccionado(s)
        </span>
      </div>

      {lista.length === 0 ? (
        <Vacio>
          {soloSinImprimir ? (
            <>
              No hay recibos ni planillas pendientes de imprimir.
              <div style={{ fontSize: 12.5, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
                Un documento que ya se marcó como impreso sale de esta lista,
                pero no se borra. Quita <strong>Solo sin imprimir</strong> para
                verlo y volver a imprimirlo.
              </div>
              <Boton variante="suave" onClick={() => setSoloSinImprimir(false)}
                     style={{ marginTop: 12 }}>
                Ver también los ya impresos
              </Boton>
            </>
          ) : (
            'No hay recibos ni planillas registrados en este rango.'
          )}
        </Vacio>
      ) : (
        Object.entries(porTipo).map(([tipo, docsTipo]) => {
          const f = FORMATOS[tipo] || { porHoja: 1, etiqueta: tipo };
          const idsTipo = docsTipo.map((d) => d.id);
          const todosElegidos = idsTipo.every((id) => elegidos.has(id));

          return (
            <div key={tipo} style={{ marginBottom: 22 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 9, gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                    {f.etiqueta}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted }}>
                    {f.porHoja} por hoja A4 · {docsTipo.length} documento(s)
                  </div>
                </div>
                <button
                  onClick={() => {
                    const s = new Set(elegidos);
                    idsTipo.forEach((id) => (todosElegidos ? s.delete(id) : s.add(id)));
                    setElegidos(s);
                  }}
                  style={{
                    fontSize: 12, padding: '5px 11px', borderRadius: 13,
                    border: `1px solid ${T.border}`, background: T.paper,
                    color: T.text, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  {todosElegidos ? 'Ninguno' : 'Todos'}
                </button>
              </div>

              <div style={{ display: 'grid', gap: 7 }}>
                {docsTipo.map((d) => {
                  const sel = elegidos.has(d.id);
                  return (
                    <Card key={d.id} onClick={() => alternar(d.id)} style={{
                      padding: 12,
                      borderColor: sel ? T.navy : T.border,
                      background: sel ? T.warm : T.paper,
                    }}>
                      <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                        <span style={{
                          width: 19, height: 19, borderRadius: 4, flexShrink: 0,
                          border: `2px solid ${sel ? T.navy : T.border}`,
                          background: sel ? T.navy : 'transparent',
                          color: '#fff', fontSize: 12, lineHeight: '15px',
                          textAlign: 'center', fontWeight: 700,
                        }}>
                          {sel ? '✓' : ''}
                        </span>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {d.reciboSerie ? `${d.reciboSerie}-` : ''}{d.numeroDocumento || d.id}
                            {d.impresiones > 0 && (
                              <span style={{
                                marginLeft: 7, fontSize: 10, color: T.muted,
                                fontWeight: 400,
                              }}>
                                ya impreso {d.impresiones}×
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                            {fmt.fecha(d.fecha)}
                            {d.autor ? ` · ${d.autor}` : ''}
                            {d.ovId ? ` · ${d.ovId}` : ''}
                          </div>
                        </div>

                        <div style={{
                          fontSize: 13, fontWeight: 600,
                          color: T.navy, whiteSpace: 'nowrap',
                        }}>
                          {fmt.money(d.total, d.moneda)}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {elegidos.size > 0 && (
        <div style={{
          position: 'sticky', bottom: 0, background: T.cream,
          padding: '12px 0', borderTop: `1px solid ${T.border}`,
        }}>
          <Boton variante="acento" onClick={preparar} style={{ width: '100%' }}>
            Preparar {elegidos.size} para imprimir
          </Boton>
        </div>
      )}

      {onCerrar && (
        <Boton variante="suave" onClick={onCerrar} style={{ width: '100%', marginTop: 12 }}>
          Volver
        </Boton>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function HojaImpresion({ documentos, onCerrar, onImpreso }) {
  const tipo = documentos[0]?.gasto?.tipoDocumento;
  const f = FORMATOS[tipo] || { porHoja: 1, alto: 260 };

  // Agrupar en tandas del tamaño de una hoja
  const hojas = [];
  for (let i = 0; i < documentos.length; i += f.porHoja) {
    hojas.push(documentos.slice(i, i + f.porHoja));
  }

  // ── Por qué ya no se marca solo ───────────────────────────────────
  //
  // `window.print()` no dice si salió papel. Antes se marcaba a los 900
  // ms pasara lo que pasara: si la impresión salía en blanco o la
  // persona cancelaba, el documento igual desaparecía de la lista de
  // pendientes. Es lo que le pasó a Dulce con la planilla GAS-2026-J5979.
  //
  // El navegador no puede saberlo. Quien sí puede es la persona que
  // tiene la hoja en la mano, así que se le pregunta.
  const [preguntando, setPreguntando] = useState(false);


  // ── Candado contra la hoja en blanco ──────────────────────────────
  //
  // El CSS de impresión oculta `body > *:not(.hoja)`. Eso solo es
  // correcto si la hoja es hija DIRECTA de <body>, que es lo que el
  // portal garantiza. Si algún día alguien la vuelve a anidar, la
  // regla ocultaría a su ancestro —todo— y volvería a imprimirse una
  // página en blanco, sin ningún error visible.
  //
  // Esto lo comprueba antes de imprimir y lo dice, en vez de dejar que
  // alguien descubra el problema en el papel.
  const hojaRef = useRef(null);
  const verificarMontaje = () => {
    const n = hojaRef.current;
    if (n && n.parentElement !== document.body) {
      // No se bloquea la impresión: puede que el CSS cambie y funcione
      // igual. Pero queda dicho, y la confirmación de después atrapa el
      // resultado real.
      console.warn(
        '[imprimir] la hoja no es hija directa de <body>: el CSS de '
        + 'impresion la va a ocultar y la pagina saldra en blanco.',
      );
      return false;
    }
    return true;
  };

  const [malMontada, setMalMontada] = useState(false);

  const imprimir = () => {
    setMalMontada(!verificarMontaje());
    window.print();
    // Cuando el diálogo se cierra —haya impreso o no— se pregunta.
    setPreguntando(true);
  };

  // La hoja se monta DIRECTAMENTE en <body>.
  //
  // El CSS de impresión dice `body > *:not(.hoja) { display: none }`,
  // que estaba escrito para esto. Pero la hoja vivía dentro de
  // <div id="root">, y #root no tiene la clase: la regla lo ocultaba a
  // él, es decir TODO, incluida la hoja. De ahí la página en blanco con
  // solo el encabezado del navegador.
  //
  // Con el portal la regla es literalmente cierta y hace lo que dice.
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 115, background: '#fff', overflowY: 'auto' }}
         className="hoja" ref={hojaRef}>
      <style>{ESTILO_LOTE}</style>

      {preguntando && (
        <ConfirmarImpresion
          cantidad={documentos.length}
          malMontada={malMontada}
          onSalioBien={() => { setPreguntando(false); onImpreso(); }}
          onReintentar={() => setPreguntando(false)}
        />
      )}

      <div className="no-imprimir" style={{
        position: 'sticky', top: 0, zIndex: 5, background: T.navy,
        padding: '11px 16px', display: 'flex', gap: 8,
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        <Boton variante="suave" onClick={onCerrar}
               style={{ fontSize: 13, padding: '6px 13px' }}>
          Volver
        </Boton>
        <Boton variante="acento" onClick={imprimir}
               style={{ fontSize: 13, padding: '6px 13px' }}>
          Imprimir
        </Boton>
        <span style={{ color: T.brass, fontSize: 12 }}>
          {documentos.length} documento(s) · {hojas.length} hoja(s) A4
        </span>
      </div>

      <div className="no-imprimir" style={{ padding: '12px 16px 0' }}>
        <Aviso tipo="info">
          Imprime, firma a mano cada documento y corta por las líneas punteadas.
          Después fotografía el papel firmado y adjúntalo al gasto: ese es el
          sustento que respalda el costo cuando viaja a SAP.
        </Aviso>
      </div>

      <div className="area-impresion">
        {hojas.map((hoja, i) => (
          <div key={i} className="hoja-a4">
            {hoja.map((d) => (
              <div key={d.gasto.id} className="recorte" style={{ minHeight: `${f.alto}mm` }}>
                {tipo === 'planilla_movilidad'
                  ? <PlanillaCompacta d={d} />
                  : <ReciboCompacto d={d} />}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}


// ── ¿Salió bien? ────────────────────────────────────────────────────
//
// Lo único que puede responder esto es quien tiene la hoja delante.
// Mientras no conteste, los documentos siguen en la lista de
// pendientes: perder un papel es recuperable, perder el registro de
// que falta imprimirlo no.
function ConfirmarImpresion({ cantidad, malMontada, onSalioBien, onReintentar }) {
  return (
    <div className="no-imprimir" style={{
      position: 'fixed', inset: 0, zIndex: 130, background: 'rgba(10,15,26,.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 22,
        width: '100%', maxWidth: 400,
      }}>
        <div style={{
          fontSize: 18, fontWeight: 600, color: T.navy, marginBottom: 6,
        }}>
          ¿Salió el papel?
        </div>
        <div style={{
          fontSize: 13, color: T.muted, marginBottom: 18, lineHeight: 1.5,
        }}>
          Revisa que {cantidad === 1 ? 'la hoja tenga' : 'las hojas tengan'} el
          contenido completo, no solo el encabezado. Si está en blanco o la
          impresión falló, los documentos se quedan en la lista de pendientes.
        </div>

        {malMontada && (
          <Aviso tipo="bad">
            La hoja no quedó montada donde el estilo de impresión la espera.
            Es muy probable que haya salido en blanco: responde que no y
            avisa a soporte.
          </Aviso>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Boton variante="acento" onClick={onSalioBien} style={{ width: '100%' }}>
            Sí, {cantidad === 1 ? 'salió completa' : 'salieron completas'}
          </Boton>
          <Boton variante="suave" onClick={onReintentar} style={{ width: '100%' }}>
            No, dejarlos pendientes
          </Boton>
        </div>
      </div>
    </div>
  );
}


// ── Recibo compacto: un tercio de A4 ────────────────────────────────
function ReciboCompacto({ d }) {
  const g = d.gasto;
  return (
    <div className="doc">
      <div className="doc-cab">
        <img src="/marca/logo-barra.png" alt="SICREA" className="doc-logo" />
        <div className="doc-emisor">
          <strong>SICREA S.A.C.</strong> · RUC 20136792319<br />
          Calle A Mz. T Lt. 4 Urb. Nuevo Lurín · Lurín, Lima
        </div>
        <div className="doc-num">
          <div className="doc-tipo">RECIBO</div>
          <div className="doc-serie">
            {g.reciboSerie || 'AD'}-{g.numeroDocumento || '______'}
          </div>
        </div>
      </div>

      <div className="doc-linea">
        Recibí de <strong>SICREA S.A.C.</strong> la cantidad de
        <span className="doc-monto">{fmt.money(g.total, g.moneda)}</span>
      </div>

      <table className="doc-campos">
        <tbody>
          <tr>
            <td>Fecha</td><td>{fmt.fecha(g.fecha)}</td>
            <td>OV</td><td>{g.ovId || 'Sin OV'}</td>
          </tr>
          <tr>
            <td>Concepto</td><td colSpan={3}>{g.concepto || ''}</td>
          </tr>
          <tr>
            <td>Cuenta</td><td>{g.cuentaPcge} · {d.cuentaNombre}</td>
            <td>Centro</td><td>{g.centro}</td>
          </tr>
          {g.beneficiarioNombre && (
            <tr>
              <td>Beneficiario</td><td>{g.beneficiarioNombre}</td>
              <td>DNI</td><td>{g.beneficiarioDni || ''}</td>
            </tr>
          )}
          {/* Quien gasto el dinero, cuando no es quien lo recibio. */}
          {(d.consumidor?.nombre || g.consumidorNombre) && (
            <tr>
              <td>Quién consumió</td>
              <td colSpan={3}>{d.consumidor?.nombre || g.consumidorNombre}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="doc-firmas">
        <div className="doc-firma">
          <div className="doc-raya" />
          Entregado por · {d.autor?.nombre || ''}
        </div>
        <div className="doc-firma">
          <div className="doc-raya" />
          Recibí conforme · DNI ______________
        </div>
      </div>

      <div className="doc-pie">
        {g.id} · huella {String(d.huella || '').slice(0, 10)} · SICREA RINDE
      </div>
    </div>
  );
}

// Quien va en el renglon "Trabajador".
//
// El Reglamento del Impuesto a la Renta pide que la planilla nombre y
// la firme el trabajador que INCURRIO en el gasto. Si Dulce recibe el
// dinero del encargo y el viaje lo hizo un operario, el que tiene que
// salir ahi es el operario, no ella.
function trabajadorDe(d) {
  const g = d.gasto || {};
  if (d.consumidor?.nombre) {
    return { nombre: d.consumidor.nombre, dni: d.consumidor.dni };
  }
  if (g.consumidorNombre) return { nombre: g.consumidorNombre, dni: null };
  if (d.beneficiario?.nombre) {
    return { nombre: d.beneficiario.nombre, dni: d.beneficiario.dni };
  }
  return { nombre: d.autor?.nombre || '', dni: null };
}

// ── Planilla compacta: media A4 ─────────────────────────────────────
function PlanillaCompacta({ d }) {
  const g = d.gasto;
  const trabajador = trabajadorDe(d);
  const administra = d.beneficiario?.nombre || d.autor?.nombre || '';
  const administraOtro = Boolean(
    (d.consumidor?.nombre || g.consumidorNombre)
    && administra && administra !== trabajador.nombre,
  );
  return (
    <div className="doc">
      <div className="doc-cab">
        <img src="/marca/logo-barra.png" alt="SICREA" className="doc-logo" />
        <div className="doc-emisor">
          <strong>SICREA S.A.C.</strong> · RUC 20136792319<br />
          Calle A Mz. T Lt. 4 Urb. Nuevo Lurín · Lurín, Lima
        </div>
        <div className="doc-num">
          <div className="doc-tipo">PLANILLA DE MOVILIDAD</div>
          <div className="doc-serie">N° {g.numeroDocumento || '______'}</div>
        </div>
      </div>

      <table className="doc-campos">
        <tbody>
          <tr>
            <td>Trabajador que se movilizó</td><td>{trabajador.nombre}</td>
            <td>OV</td><td>{g.ovId || 'Sin OV'}</td>
          </tr>
          {administraOtro && (
            <tr>
              <td>Rinde / administra</td><td colSpan={3}>{administra}</td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="doc-tabla">
        <thead>
          <tr>
            <th>Día</th><th>De</th><th>A ó para</th><th>Motivo</th><th>Importe</th>
          </tr>
        </thead>
        <tbody>
          {(d.trayectos || []).map((t) => (
            <tr key={t.id}>
              <td>{fmt.fecha(t.fecha)}</td>
              <td>{t.origen}</td>
              <td>{t.destino}</td>
              <td>{t.motivo}</td>
              <td className="der">{Number(t.importe).toFixed(2)}</td>
            </tr>
          ))}
          {/* Renglones en blanco para poder anotar a mano en obra */}
          {Array.from({ length: Math.max(0, 4 - (d.trayectos || []).length) }).map((_, i) => (
            <tr key={`v${i}`}>
              <td>&nbsp;</td><td /><td /><td /><td />
            </tr>
          ))}
          <tr>
            <td colSpan={4} className="der negrita">TOTAL S/</td>
            <td className="der negrita">{Number(g.total).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className="doc-medio">
        Medio utilizado: {['bus', 'colectivo', 'micro', 'taxi'].map((m) => (
          <span key={m} className="doc-casilla">
            <span className={g.medioTransporte === m ? 'marcada' : ''}>
              {g.medioTransporte === m ? '×' : ' '}
            </span> {m}
          </span>
        ))}
      </div>

      <div className="doc-firmas">
        <div className="doc-firma">
          <div className="doc-raya" />
          {trabajador.nombre || 'Firma del trabajador'}
          {' · DNI '}{trabajador.dni || '______________'}
        </div>
        <div className="doc-firma">
          <div className="doc-raya" />
          V°B° Jefe inmediato
        </div>
      </div>

      <div className="doc-pie">
        {g.id} · huella {String(d.huella || '').slice(0, 10)} · SICREA RINDE
      </div>
    </div>
  );
}

// ── Estilos ─────────────────────────────────────────────────────────
const ESTILO_LOTE = `
.area-impresion {
  background: #e8e8e4;
  padding: 16px 0;
}

/* Cada hoja simula una A4 en pantalla para ver como quedara cortada */
.hoja-a4 {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto 16px;
  padding: 10mm 12mm;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 10px rgba(0,0,0,.15);
}

/* Linea de corte entre documentos */
.recorte {
  border-bottom: 1px dashed #999;
  padding-bottom: 5mm;
  margin-bottom: 5mm;
  box-sizing: border-box;
}
.recorte:last-child { border-bottom: none; margin-bottom: 0; }

.doc {
  font-family: system-ui, -apple-system, sans-serif;
  color: #111;
  font-size: 9pt;
}
.doc-cab {
  display: flex; align-items: flex-start; gap: 8px;
  border-bottom: 1.5px solid #0D2547;
  padding-bottom: 4px; margin-bottom: 6px;
}
.doc-logo { height: 22px; }
.doc-emisor { font-size: 6.5pt; color: #444; line-height: 1.35; flex: 1; }
.doc-num { text-align: right; white-space: nowrap; }
.doc-tipo { font-size: 7pt; font-weight: 700; color: #0D2547; letter-spacing: .04em; }
.doc-serie { font-family: monospace; font-size: 11pt; font-weight: 700; color: #B8241C; }

.doc-linea { font-size: 9pt; margin-bottom: 6px; }
.doc-monto { font-size: 12pt; font-weight: 700; margin-left: 6px; }

.doc-campos { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
.doc-campos td { padding: 2px 4px 2px 0; font-size: 8pt; vertical-align: top; }
.doc-campos td:nth-child(odd) { color: #666; width: 62px; font-size: 7pt; }
.doc-campos td:nth-child(even) { border-bottom: 1px dotted #bbb; }

.doc-tabla { width: 100%; border-collapse: collapse; font-size: 7.5pt; margin-bottom: 5px; }
.doc-tabla th {
  border: 1px solid #888; padding: 2px 4px; background: #eceff4;
  font-size: 7pt; text-align: left; color: #0D2547;
}
.doc-tabla td { border: 1px solid #bbb; padding: 2px 4px; height: 13px; }
.doc-tabla .der { text-align: right; }
.doc-tabla .negrita { font-weight: 700; }

.doc-medio { font-size: 7.5pt; margin-bottom: 8px; }
.doc-casilla { margin-left: 10px; }
.doc-casilla span {
  display: inline-block; width: 11px; height: 11px;
  border: 1px solid #555; text-align: center;
  line-height: 10px; font-weight: 700; vertical-align: -1px;
}

.doc-firmas { display: flex; gap: 24px; margin-top: 10px; }
.doc-firma { flex: 1; font-size: 7pt; color: #444; text-align: center; }
.doc-raya { border-top: 1px solid #333; margin-bottom: 3px; margin-top: 16px; }

.doc-pie { font-size: 5.5pt; color: #999; margin-top: 5px; }

@media print {
  /* Solo la hoja: el resto de la aplicacion estorba */
  body > *:not(.hoja) { display: none !important; }
  .no-imprimir { display: none !important; }
  .hoja { position: static !important; }
  .area-impresion { background: none; padding: 0; }

  .hoja-a4 {
    width: auto; min-height: 0; margin: 0; padding: 0;
    box-shadow: none;
    /* Cada tanda en su propia hoja, sin partir documentos */
    page-break-after: always;
  }
  .hoja-a4:last-child { page-break-after: auto; }
  .recorte { page-break-inside: avoid; }

  @page { size: A4 portrait; margin: 10mm 12mm; }
}
`;
